
import { useState, useEffect } from 'react';

interface ReportData {
  totalUsers: number;
  totalReservations: number;
  activeReservations: number;
  monthlyReservations: number;
  userGrowth: number;
  reservationGrowth: number;
}

interface MonthlyData {
  month: string;
  users: number;
  reservations: number;
}

interface ActivitySummary {
  totalLogins: number;
  passwordChanges: number;
  activeUsers: number;
  topUsers: Array<{
    userId: number;
    name: string;
    email: string;
    loginCount: number;
  }>;
  dailyActivity: Array<{
    date: string;
    logins: number;
  }>;
}

interface UserActivity {
  id: number;
  activity_type: string;
  activity_description: string;
  ip_address: string;
  created_at: string;
  users: {
    name: string;
    email: string;
  };
}

interface UserStat {
  id: number;
  name: string;
  email: string;
  last_login_at: string | null;
  password_changed_at: string | null;
  login_count: number;
  created_at: string;
}

export default function Reports() {
  const [reportData, setReportData] = useState<ReportData>({
    totalUsers: 0,
    totalReservations: 0,
    activeReservations: 0,
    monthlyReservations: 0,
    userGrowth: 0,
    reservationGrowth: 0
  });
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [activitySummary, setActivitySummary] = useState<ActivitySummary | null>(null);
  const [recentActivities, setRecentActivities] = useState<UserActivity[]>([]);
  const [userStats, setUserStats] = useState<UserStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const [activeReportTab, setActiveReportTab] = useState('system');

  const fetchReportData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ period: selectedPeriod }),
      });

      const data = await response.json();
      if (response.ok) {
        setReportData(data.summary);
        setMonthlyData(data.monthlyData || []);
      } else {
        console.error('Error fetching reports:', data.error);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityReports = async (reportType: 'summary' | 'detailed') => {
    try {
      setActivityLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/admin-activity-reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ period: selectedPeriod, reportType }),
      });

      const data = await response.json();
      if (response.ok) {
        if (reportType === 'summary') {
          setActivitySummary(data.summary);
        } else {
          setRecentActivities(data.recentActivities || []);
          setUserStats(data.userStats || []);
        }
      } else {
        console.error('Error fetching activity reports:', data.error);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [selectedPeriod]);

  useEffect(() => {
    if (activeReportTab === 'activity') {
      fetchActivityReports('summary');
      fetchActivityReports('detailed');
    }
  }, [activeReportTab, selectedPeriod]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Hiç';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityTypeText = (type: string) => {
    switch (type) {
      case 'login': return 'Giriş Yaptı';
      case 'password_change': return 'Şifre Değiştirdi';
      case 'logout': return 'Çıkış Yaptı';
      case 'reservation_create': return 'Rezervasyon Oluşturdu';
      case 'reservation_update': return 'Rezervasyon Güncelledi';
      case 'reservation_delete': return 'Rezervasyon Sildi';
      default: return type;
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'login': return 'ri-login-box-line';
      case 'password_change': return 'ri-key-line';
      case 'logout': return 'ri-logout-box-line';
      case 'reservation_create': return 'ri-calendar-check-line';
      case 'reservation_update': return 'ri-edit-line';
      case 'reservation_delete': return 'ri-delete-bin-line';
      default: return 'ri-information-line';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'login': return 'text-green-600 bg-green-50';
      case 'password_change': return 'text-blue-600 bg-blue-50';
      case 'logout': return 'text-gray-600 bg-gray-50';
      case 'reservation_create': return 'text-purple-600 bg-purple-50';
      case 'reservation_update': return 'text-orange-600 bg-orange-50';
      case 'reservation_delete': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-blue-600 mr-3"></i>
          <span className="text-gray-600">Raporlar yükleniyor...</span>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Toplam Kullanıcı',
      value: reportData.totalUsers,
      icon: 'ri-user-line',
      color: 'blue',
      growth: reportData.userGrowth
    },
    {
      title: 'Toplam Rezervasyon',
      value: reportData.totalReservations,
      icon: 'ri-calendar-line',
      color: 'green',
      growth: reportData.reservationGrowth
    },
    {
      title: 'Aktif Rezervasyon',
      value: reportData.activeReservations,
      icon: 'ri-calendar-check-line',
      color: 'purple',
      growth: 0
    },
    {
      title: 'Aylık Rezervasyon',
      value: reportData.monthlyReservations,
      icon: 'ri-calendar-event-line',
      color: 'orange',
      growth: 0
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Sistem Raporları</h2>
            <p className="text-sm text-gray-600 mt-1">Kullanıcı ve rezervasyon istatistikleri</p>
          </div>
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Dönem:</label>
            <div className="relative">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm"
              >
                <option value="7">Son 7 Gün</option>
                <option value="30">Son 30 Gün</option>
                <option value="90">Son 90 Gün</option>
                <option value="365">Son 1 Yıl</option>
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveReportTab('system')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeReportTab === 'system'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-dashboard-line mr-2"></i>
              Sistem Raporları
            </button>
            <button
              onClick={() => setActiveReportTab('activity')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeReportTab === 'activity'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-pulse-line mr-2"></i>
              Kullanım Raporları
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeReportTab === 'system' && (
            <div className="space-y-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value.toLocaleString()}</p>
                        {stat.growth !== 0 && (
                          <div className={`flex items-center mt-2 text-sm ${
                            stat.growth > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            <i className={`${stat.growth > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} mr-1`}></i>
                            <span>{Math.abs(stat.growth)}%</span>
                          </div>
                        )}
                      </div>
                      <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                        <i className={`${stat.icon} text-xl text-${stat.color}-600`}></i>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Trend */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Aylık Trend</h3>
                  <div className="space-y-4">
                    {monthlyData.length > 0 ? (
                      monthlyData.map((data, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">{data.month}</span>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                              <span className="text-sm text-gray-600">{data.users} kullanıcı</span>
                            </div>
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                              <span className="text-sm text-gray-600">{data.reservations} rezervasyon</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <i className="ri-bar-chart-line text-4xl text-gray-400 mb-4"></i>
                        <p className="text-gray-500">Henüz veri bulunmuyor</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Activity Summary */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Aktivite Özeti</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-user-add-line text-blue-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Yeni Kullanıcılar</span>
                      </div>
                      <span className="text-sm font-bold text-blue-600">+{reportData.userGrowth || 0}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-calendar-check-line text-green-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Tamamlanan Rezervasyonlar</span>
                      </div>
                      <span className="text-sm font-bold text-green-600">{reportData.totalReservations - reportData.activeReservations}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-calendar-event-line text-orange-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Bekleyen Rezervasyonlar</span>
                      </div>
                      <span className="text-sm font-bold text-orange-600">{reportData.activeReservations}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center">
                        <i className="ri-time-line text-purple-600 mr-3"></i>
                        <span className="text-sm font-medium text-gray-700">Ortalama Kullanım</span>
                      </div>
                      <span className="text-sm font-bold text-purple-600">
                        {reportData.totalUsers > 0 ? (reportData.totalReservations / reportData.totalUsers).toFixed(1) : '0'} rez/kullanıcı
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeReportTab === 'activity' && (
            <div className="space-y-6">
              {activityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <i className="ri-loader-4-line animate-spin text-2xl text-blue-600 mr-3"></i>
                  <span className="text-gray-600">Kullanım raporları yükleniyor...</span>
                </div>
              ) : (
                <>
                  {/* Activity Statistics */}
                  {activitySummary && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Toplam Giriş</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">{activitySummary.totalLogins.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <i className="ri-login-box-line text-xl text-green-600"></i>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Aktif Kullanıcı</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">{activitySummary.activeUsers.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            <i className="ri-user-line text-xl text-blue-600"></i>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Şifre Değiştiren</p>
                            <p className="text-2xl font-bold text-gray-900 mt-2">{activitySummary.passwordChanges.toLocaleString()}</p>
                          </div>
                          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                            <i className="ri-key-line text-xl text-purple-600"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Users */}
                    {activitySummary && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">En Aktif Kullanıcılar</h3>
                        <div className="space-y-3">
                          {activitySummary.topUsers.length > 0 ? (
                            activitySummary.topUsers.map((user, index) => (
                              <div key={user.userId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                    <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                    <p className="text-xs text-gray-500">{user.email}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-gray-900">{user.loginCount}</p>
                                  <p className="text-xs text-gray-500">giriş</p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8">
                              <i className="ri-user-line text-4xl text-gray-400 mb-4"></i>
                              <p className="text-gray-500">Henüz aktivite bulunmuyor</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Daily Activity Chart */}
                    {activitySummary && (
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Günlük Giriş Aktivitesi</h3>
                        <div className="space-y-3">
                          {activitySummary.dailyActivity.length > 0 ? (
                            activitySummary.dailyActivity.slice(-7).map((day, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{day.date}</span>
                                <div className="flex items-center">
                                  <div className="w-20 bg-gray-200 rounded-full h-2 mr-3">
                                    <div 
                                      className="bg-blue-500 h-2 rounded-full" 
                                      style={{ 
                                        width: `${Math.max(5, (day.logins / Math.max(...activitySummary.dailyActivity.map(d => d.logins))) * 100)}%` 
                                      }}
                                    ></div>
                                  </div>
                                  <span className="text-sm text-gray-600 w-8 text-right">{day.logins}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-8">
                              <i className="ri-bar-chart-line text-4xl text-gray-400 mb-4"></i>
                              <p className="text-gray-500">Henüz veri bulunmuyor</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recent Activities */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Son Aktiviteler</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {recentActivities.length > 0 ? (
                        recentActivities.map((activity) => (
                          <div key={activity.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${getActivityColor(activity.activity_type)}`}>
                                <i className={`${getActivityIcon(activity.activity_type)} text-sm`}></i>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{activity.users.name}</p>
                                <p className="text-xs text-gray-500">{activity.users.email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">{getActivityTypeText(activity.activity_type)}</p>
                              <p className="text-xs text-gray-500">{formatDate(activity.created_at)}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <i className="ri-history-line text-4xl text-gray-400 mb-4"></i>
                          <p className="text-gray-500">Henüz aktivite bulunmuyor</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User Statistics Table */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Kullanıcı İstatistikleri</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Toplam Giriş</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Son Giriş</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Şifre Değiştirme</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kayıt Tarihi</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {userStats.length > 0 ? (
                            userStats.map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                    <div className="text-sm text-gray-500">{user.email}</div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{user.login_count || 0}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{formatDate(user.last_login_at)}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{formatDate(user.password_changed_at)}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{formatDate(user.created_at)}</span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                                Henüz kullanıcı bulunmuyor
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rapor Dışa Aktarma</h3>
        <div className="flex flex-wrap gap-3">
          <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
            <i className="ri-file-excel-line mr-2"></i>
            Excel Olarak İndir
          </button>
          <button className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap">
            <i className="ri-file-pdf-line mr-2"></i>
            PDF Olarak İndir
          </button>
          <button className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap">
            <i className="ri-mail-line mr-2"></i>
            E-posta Gönder
          </button>
        </div>
      </div>
    </div>
  );
}
